import { Member, MemberFeeSummary } from '../services/member.service';

export interface MemberArrears {
  monthsBehind: number;
  totalOutstanding: number;
}

/**
 * Walks backward month-by-month from the current month, counting how many consecutive
 * periods (including the current one) are not fully paid, stopping at the member's join
 * date or the first fully-paid month. Shared by the self-service dues nudge and the member
 * detail page's "current standing" tile so the two never drift out of sync.
 */
export function computeMemberArrears(member: Member, feeSummary: MemberFeeSummary | null): MemberArrears {
  const joined = member.joinedDate ? new Date(member.joinedDate) : null;
  const joinedYear = joined?.getFullYear() ?? null;
  const joinedMonth = joined ? joined.getMonth() + 1 : null;

  let monthsBehind = 0;
  let totalOutstanding = 0;
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth() + 1;

  while (monthsBehind <= 24) {
    if (joinedYear != null && joinedMonth != null) {
      if (year < joinedYear || (year === joinedYear && month < joinedMonth)) break;
    }

    const yearData = feeSummary?.byYear.find(y => y.year === year);
    const monthData = yearData?.months.find(mm => mm.month === month);
    const expected = monthData?.expected ?? member.monthlyFee ?? 0;
    const paid = monthData?.totalPaid ?? 0;
    const status = monthData?.status ?? 'Empty';

    if (expected > 0 && status !== 'Paid') {
      monthsBehind++;
      totalOutstanding += Math.max(expected - paid, 0);
    } else {
      break;
    }

    month--;
    if (month < 1) {
      month = 12;
      year--;
    }
  }

  return { monthsBehind, totalOutstanding };
}
